import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  GitCompare,
  Sparkles,
  Send,
  X,
  ChevronDown,
  ExternalLink,
} from "lucide-react";

interface FreelancerMultiSelectBarProps {
  count: number;
  onClear: () => void;
  onCompare?: () => void;
  /** Quando em modo seleção para vaga: callback que conclui o envio. */
  onSendToVaga?: () => void;
  /** Quando em modo seleção para grupo de avaliação. */
  onSendToEvaluationGroup?: () => void;
  /** Fora dos modos de seleção: abre o diálogo para escolher a vaga de destino. */
  onEnviarParaVaga?: () => void;
  /** Abrir portfolio E LinkedIn de cada selecionado em novas abas. */
  onOpenLinks?: () => void;
  vagaId?: string;
}

/**
 * Barra sticky no rodapé com ações em massa para os freelancers selecionados.
 * Aparece somente quando count > 0. As ações de IA são placeholders ("em breve").
 *
 * Renderizada via portal em document.body + z-50: como os cards do kanban usam
 * transforms (scale no drag), um `position: fixed` aninhado viraria relativo ao
 * ancestral transformado e a barra "sumia" no rodapé. O portal garante que ela
 * fique sempre fixa em relação à viewport.
 */
export function FreelancerMultiSelectBar({
  count,
  onClear,
  onCompare,
  onSendToVaga,
  onSendToEvaluationGroup,
  onEnviarParaVaga,
  onOpenLinks,
  vagaId,
}: FreelancerMultiSelectBarProps) {
  if (count === 0) return null;

  const bar = (
    <TooltipProvider>
      {/* z-60 fica acima de toolbars/popovers (z-50). pb-safe respeita a barra
          inferior em iPhone. pointer-events-none no wrapper deixa cliques nas
          áreas vazias passarem para o conteúdo atrás. */}
      <div
        className="fixed inset-x-0 bottom-0 z-60 flex justify-center pb-4 pointer-events-none"
        style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
      >
      <div className="pointer-events-auto bg-background border-2 shadow-2xl rounded-full pl-4 pr-2 py-1.5 flex items-center gap-2 max-w-[calc(100vw-2rem)]">
        <span className="text-sm font-medium whitespace-nowrap">
          {count} selecionado{count > 1 ? "s" : ""}
        </span>
        <span className="text-muted-foreground">·</span>

        {onCompare && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onCompare}
                disabled={count < 2}
                className="gap-1.5"
              >
                <GitCompare className="h-4 w-4" />
                Comparar
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {count < 2
                ? "Selecione ao menos 2 para comparar"
                : `Comparar ${count} freelancers lado a lado`}
            </TooltipContent>
          </Tooltip>
        )}

        {onOpenLinks && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onOpenLinks}
                className="gap-1.5"
              >
                <ExternalLink className="h-4 w-4" />
                Abrir links
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Abrir portfolio e LinkedIn de cada selecionado em novas abas
            </TooltipContent>
          </Tooltip>
        )}

        {onSendToVaga && vagaId && (
          <Button size="sm" onClick={onSendToVaga} className="gap-1.5">
            <Send className="h-4 w-4" />
            Adicionar à vaga
          </Button>
        )}

        {onEnviarParaVaga && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onEnviarParaVaga}
                className="gap-1.5"
              >
                <Send className="h-4 w-4" />
                Enviar para vaga
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Vincular os selecionados aos candidatos de uma vaga
            </TooltipContent>
          </Tooltip>
        )}

        {onSendToEvaluationGroup && (
          <Button size="sm" onClick={onSendToEvaluationGroup} className="gap-1.5">
            <Send className="h-4 w-4" />
            Adicionar ao grupo
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-1.5">
              <Sparkles className="h-4 w-4 text-purple-500" />
              Ações IA
              <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel>Em breve</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              Comparar com a vaga atual
            </DropdownMenuItem>
            <DropdownMenuItem disabled>Resumir esses freelancers</DropdownMenuItem>
            <DropdownMenuItem disabled>Sugerir o melhor match</DropdownMenuItem>
            <DropdownMenuItem disabled>Gerar mensagem de outreach</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClear}
              aria-label="Limpar seleção"
              className="rounded-full h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Limpar seleção</TooltipContent>
        </Tooltip>
      </div>
      </div>
    </TooltipProvider>
  );

  return createPortal(bar, document.body);
}
